/** Phase 5 part 2 on the web: the campaign wiki, homebrew (campaign + Admin global). */
import { useCallback, useEffect, useMemo, useState } from "react";
import { HOMEBREW_KINDS, type Homebrew, type HomebrewInput, type HomebrewKind, type HomebrewResponse, type WikiIndex, type WikiPage } from "@dm/shared";
import { api } from "./api.js";
import { WikiPictures } from "./Images.js";
import { useGame } from "./game.js";

const KIND_LABEL: Record<string, string> = { npc: "People", place: "Places", faction: "Factions", item: "Items", quest: "Quests" };
const KIND_ONE: Record<string, string> = { npc: "Person", place: "Place", faction: "Faction", item: "Item", quest: "Quest" };
const KIND_ORDER = ["npc", "place", "faction", "item", "quest"];

// ------------------------------------------------------------------ wiki

export function WikiView({ id }: { id?: number }) {
  const { campaign, fail } = useGame();
  const [index, setIndex] = useState<WikiIndex | null>(null);
  const [page, setPage] = useState<WikiPage | null>(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    api.wiki(campaign.id).then(setIndex, fail);
  }, [campaign.id, fail]);
  useEffect(() => {
    if (!id) return setPage(null);
    api.wikiPage(campaign.id, id).then(setPage, (err) => {
      setPage(null);
      fail(err);
    });
  }, [campaign.id, id, fail]);
  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = (index?.entries ?? []).filter((e) => !needle || e.title.toLowerCase().includes(needle) || e.snippet.toLowerCase().includes(needle));
    return KIND_ORDER.map((k) => ({ kind: k, items: list.filter((e) => e.kind === k) })).filter((g) => g.items.length);
  }, [index, q]);
  if (!index) return <main className="muted">Loading…</main>;
  return (
    <main className="wiki">
      <aside className="panel wiki-index">
        <input type="search" placeholder="Search the wiki" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search the wiki" />
        {!index.entries.length && <p className="muted small">Nothing yet. The DM records people, places and quests as you play.</p>}
        {groups.map((g) => (
          <div key={g.kind}>
            <h4>{KIND_LABEL[g.kind]}</h4>
            <ul>
              {g.items.map((e) => (
                <li key={e.id}>
                  <a href={`#/wiki/${e.id}`} className={e.id === id ? "active" : ""}>
                    {e.title}
                  </a>
                  {e.status && e.status !== "open" && <span className="tag">{e.status}</span>}
                  {!e.knownToPlayers && <span className="tag" title="Players can't see this yet">hidden</span>}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </aside>
      <section className="panel wiki-page">
        {!page ? (
          <>
            <h3>{campaign.name} wiki</h3>
            <p className="muted">
              Everything the party knows about the world, written by the DM as you play: people, places, factions, items and quests. Pick an entry.
              {index.canEdit && " (You see hidden entries and DM secrets because you run this game; edit them on the Memory tab.)"}
            </p>
          </>
        ) : (
          <WikiEntry page={page} canEdit={index.canEdit} />
        )}
      </section>
    </main>
  );
}

function WikiEntry({ page, canEdit }: { page: WikiPage; canEdit: boolean }) {
  const e = page.entry;
  const meta = [e.attitude && `Attitude: ${e.attitude}`, e.lastSeen && `Last seen: ${e.lastSeen}`, e.status && `Status: ${e.status}`].filter(Boolean);
  return (
    <article>
      <h3>
        {e.title} <span className="muted small">{KIND_ONE[e.kind]}</span>
        {!e.knownToPlayers && <span className="tag">hidden from players</span>}
      </h3>
      {meta.length > 0 && <p className="muted small">{meta.join(" · ")}</p>}
      <WikiPictures memoryId={e.id} />
      <p className="wiki-text">{e.content || <span className="muted">No notes yet.</span>}</p>
      {canEdit && e.secrets && <p className="wiki-secret"><strong>DM only:</strong> {e.secrets}</p>}
      {page.related.length > 0 && (
        <>
          <h4>Related</h4>
          <p className="wiki-links">
            {page.related.map((r) => (
              <a key={r.id} href={`#/wiki/${r.id}`} className="tag">{r.title}</a>
            ))}
          </p>
        </>
      )}
      <h4>At the table {page.sessions.length > 0 && <span className="muted small">(sessions {page.sessions.join(", ")})</span>}</h4>
      {!page.mentions.length && <p className="muted small">Not mentioned in the transcript yet.</p>}
      <ul className="wiki-mentions">
        {page.mentions.map((m) => (
          <li key={m.lineId}>
            <span className="muted small">{m.sessionNumber ? `Session ${m.sessionNumber}` : new Date(m.at).toLocaleDateString()} · {m.speaker}:</span> {m.text}
          </li>
        ))}
      </ul>
      {canEdit && <p className="small"><a href="#/memory">Edit on the Memory tab</a></p>}
    </article>
  );
}

// ------------------------------------------------------------------ homebrew

const BLANK: HomebrewInput = { kind: "item", name: "", summary: "", details: "" };

function HomebrewForm({ initial, onSave, onCancel, saveLabel }: {
  initial: HomebrewInput; onSave: (h: HomebrewInput) => Promise<void>; onCancel?: () => void; saveLabel: string;
}) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  return (
    <form
      className="homebrew-form"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
          await onSave(f);
          if (!onCancel) setF(BLANK);
        } finally {
          setBusy(false);
        }
      }}
    >
      <div className="row">
        <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as HomebrewKind })} aria-label="Kind">
          {HOMEBREW_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <input placeholder="Name" value={f.name} maxLength={120} onChange={(e) => setF({ ...f, name: e.target.value })} aria-label="Name" />
      </div>
      <input placeholder="One-line summary (the DM always sees this)" value={f.summary} maxLength={300} onChange={(e) => setF({ ...f, summary: e.target.value })} aria-label="Summary" />
      <textarea rows={4} placeholder="Full details: stats, rules, how it works (the DM looks this up when it matters)" value={f.details} maxLength={8000}
        onChange={(e) => setF({ ...f, details: e.target.value })} aria-label="Details" />
      <div className="row-end">
        {onCancel && <button type="button" onClick={onCancel}>Cancel</button>}
        <button className="primary" disabled={busy || !f.name.trim() || !(f.summary.trim() || f.details.trim())}>{saveLabel}</button>
      </div>
    </form>
  );
}

function HomebrewCard({ h, actions, editing, onSave, onCancel }: {
  h: Homebrew; actions: React.ReactNode; editing: boolean; onSave: (x: HomebrewInput) => Promise<void>; onCancel: () => void;
}) {
  if (editing) return <div className="homebrew-card"><HomebrewForm initial={h} onSave={onSave} onCancel={onCancel} saveLabel="Save" /></div>;
  return (
    <div className={`homebrew-card ${h.status}`}>
      <div className="homebrew-head">
        <strong>{h.name}</strong> <span className="tag">{h.kind}</span>
        {h.campaignId === null && <span className="tag host" title="Applies to every campaign">all campaigns</span>}
        {h.status === "pending" && <span className="tag warn-tag">waiting for approval</span>}
        <span className="muted small"> {h.createdByName ? `by ${h.createdByName}` : ""}{h.sourceCampaignName ? ` from ${h.sourceCampaignName}` : ""}</span>
      </div>
      <p>{h.summary}</p>
      {h.details && h.details !== h.summary && (
        <details>
          <summary className="small">Details</summary>
          <p className="wiki-text small">{h.details}</p>
        </details>
      )}
      <div className="row-end">{actions}</div>
    </div>
  );
}

export function HomebrewPage() {
  const { campaign, user, fail } = useGame();
  const [data, setData] = useState<HomebrewResponse | null>(null);
  const [editing, setEditing] = useState<number | null>(null);
  const [filter, setFilter] = useState<string>("");
  const [note, setNote] = useState<string | null>(null);
  const load = useCallback(() => void api.homebrew(campaign.id).then(setData, fail), [campaign.id, fail]);
  useEffect(load, [load]);
  if (!data) return <main className="muted">Loading…</main>;
  const run = async (fn: () => Promise<unknown>, msg?: string) => {
    try {
      await fn();
      if (msg) setNote(msg);
      load();
    } catch (err) {
      fail(err);
    }
  };
  const list = data.entries.filter((h) => !filter || h.kind === filter);
  const pending = list.filter((h) => h.status === "pending");
  const live = list.filter((h) => h.status === "approved");
  const card = (h: Homebrew) => {
    const mine = h.createdBy === user.discordId && h.status === "pending";
    const canChange = h.campaignId !== null && (data.canManage || mine);
    return (
      <HomebrewCard
        key={h.id}
        h={h}
        editing={editing === h.id}
        onCancel={() => setEditing(null)}
        onSave={(x) => run(async () => { await api.updateHomebrew(campaign.id, h.id, x); setEditing(null); })}
        actions={
          <>
            {data.canManage && h.status === "pending" && (
              <>
                <button className="primary" onClick={() => run(() => api.decideHomebrew(campaign.id, h.id, true))}>Approve</button>
                <button onClick={() => run(() => api.decideHomebrew(campaign.id, h.id, false))}>Reject</button>
              </>
            )}
            {data.canManage && h.status === "approved" && h.campaignId !== null && (
              <button title="Ask the Admin to make this available in every campaign" onClick={() => run(() => api.shareHomebrew(campaign.id, h.id), "Sent to the Admin: it joins every campaign once approved.")}>
                Share with all campaigns
              </button>
            )}
            {canChange && <button onClick={() => setEditing(h.id)}>Edit</button>}
            {canChange && <button onClick={() => window.confirm(`Delete ${h.name}?`) && run(() => api.deleteHomebrew(campaign.id, h.id))}>Delete</button>}
          </>
        }
      />
    );
  };
  return (
    <main className="homebrew-page">
      <p className="muted small">
        Your table's own items, spells, monsters, classes and rules. The DM always knows the names and summaries, and reads the details when they
        come up. Homebrew marked "all campaigns" was approved by the Admin for every game.
      </p>
      {note && <div className="ok-box" onClick={() => setNote(null)}>{note}</div>}
      {data.canAdd ? (
        <section className="panel">
          <h3>Add homebrew</h3>
          {!data.canManage && <p className="muted small">The Host approves player additions before the DM uses them.</p>}
          <HomebrewForm initial={BLANK} saveLabel="Add" onSave={(h) => run(() => api.addHomebrew(campaign.id, h), data.canManage ? undefined : "Sent to the Host for approval.")} />
        </section>
      ) : (
        <p className="muted small">Only the Host adds homebrew in this campaign (they can allow players in Campaign settings).</p>
      )}
      <div className="homebrew-filter" role="group" aria-label="Filter by kind">
        <button className={!filter ? "primary" : ""} onClick={() => setFilter("")}>All</button>
        {HOMEBREW_KINDS.filter((k) => data.entries.some((h) => h.kind === k)).map((k) => (
          <button key={k} className={filter === k ? "primary" : ""} onClick={() => setFilter(k)}>{k}</button>
        ))}
      </div>
      {pending.length > 0 && (
        <section className="panel">
          <h3>Waiting for approval <span className="tag warn-tag">{pending.length}</span></h3>
          {pending.map(card)}
        </section>
      )}
      <section className="panel">
        <h3>In play</h3>
        {!live.length && <p className="muted small">No homebrew yet.</p>}
        {live.map(card)}
      </section>
    </main>
  );
}

/** Admin page: global homebrew and Hosts' "share with all campaigns" suggestions. */
export function AdminHomebrew() {
  const [data, setData] = useState<{ global: Homebrew[]; suggestions: Homebrew[] } | null>(null);
  const [editing, setEditing] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = useCallback(() => void api.adminHomebrew().then(setData, (e: Error) => setErr(e.message)), []);
  useEffect(load, [load]);
  const run = async (fn: () => Promise<unknown>) => {
    try {
      setErr(null);
      await fn();
      load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };
  if (!data) return null;
  const card = (h: Homebrew, pending: boolean) => (
    <HomebrewCard
      key={h.id}
      h={h}
      editing={editing === h.id}
      onCancel={() => setEditing(null)}
      onSave={(x) => run(async () => { await api.adminUpdateHomebrew(h.id, x); setEditing(null); })}
      actions={
        pending ? (
          <>
            <button className="primary" onClick={() => run(() => api.adminDecideHomebrew(h.id, true))}>Approve for all campaigns</button>
            <button onClick={() => run(() => api.adminDecideHomebrew(h.id, false))}>Reject</button>
          </>
        ) : (
          <>
            <button onClick={() => setEditing(h.id)}>Edit</button>
            <button onClick={() => window.confirm(`Remove ${h.name} from every campaign?`) && run(() => api.adminDeleteHomebrew(h.id))}>Delete</button>
          </>
        )
      }
    />
  );
  return (
    <>
      <section className="panel">
        <h3>Homebrew suggestions {data.suggestions.length > 0 && <span className="tag host">{data.suggestions.length} waiting</span>}</h3>
        {!data.suggestions.length && <p className="muted small">None. Hosts can share a campaign's homebrew with every campaign; it waits here for you.</p>}
        {data.suggestions.map((h) => card(h, true))}
      </section>
      <section className="panel">
        <h3>Global homebrew <span className="muted small">(every campaign)</span></h3>
        {err && <p className="warn small">{err}</p>}
        <HomebrewForm initial={BLANK} saveLabel="Add for every campaign" onSave={(h) => run(() => api.adminAddHomebrew(h))} />
        {data.global.map((h) => card(h, false))}
      </section>
    </>
  );
}
